/* ================================================
   MAUVAIS GARÇON — Minesweeper (WinXP Expert)
   30 cols × 16 rows, 99 mines.
   Split render: full _render() on state change only;
   _patchCells() + _updateHUD() on every safe click.
   ================================================ */

class MGMinesweeper {
  constructor(containerId) {
    this.el    = document.getElementById(containerId);
    this.COLS  = 20;
    this.ROWS  = 16;
    this.MINES = 208;

    this._timerInterval = null;
    this._seconds       = 0;
    this._reset();
  }

  /* ---- Setup ---- */

  _reset() {
    clearInterval(this._timerInterval);
    this._timerInterval = null;
    this._seconds       = 0;

    this.state      = 'idle';
    this.firstClick = true;
    this._clicking  = false;

    // Sync with localStorage so the modulo threshold stays in phase
    this._safeClicks = MG_UNLOCK.getSafeClicks();

    this._initArrays();
    this._render();
  }

  _initArrays() {
    this.grid     = Array.from({ length: this.ROWS }, () => Array(this.COLS).fill(0));
    this.revealed = Array.from({ length: this.ROWS }, () => Array(this.COLS).fill(false));
    this.flagged  = Array.from({ length: this.ROWS }, () => Array(this.COLS).fill(false));
  }

  _placeMines(safeR, safeC) {
    let placed = 0;
    while (placed < this.MINES) {
      const r = Math.floor(Math.random() * this.ROWS);
      const c = Math.floor(Math.random() * this.COLS);
      if (this.grid[r][c] === -1) continue;
      if (Math.abs(r - safeR) <= 1 && Math.abs(c - safeC) <= 1) continue;
      this.grid[r][c] = -1;
      placed++;
    }
    for (let r = 0; r < this.ROWS; r++)
      for (let c = 0; c < this.COLS; c++)
        if (this.grid[r][c] !== -1) this.grid[r][c] = this._adj(r, c);
  }

  _adj(r, c) {
    let n = 0;
    for (let dr = -1; dr <= 1; dr++)
      for (let dc = -1; dc <= 1; dc++) {
        const nr = r + dr, nc = c + dc;
        if (nr >= 0 && nr < this.ROWS && nc >= 0 && nc < this.COLS && this.grid[nr][nc] === -1) n++;
      }
    return n;
  }

  /* ---- Timer ---- */

  _startTimer() {
    if (this._timerInterval) return;
    this._timerInterval = setInterval(() => {
      if (this._seconds < 999) {
        this._seconds++;
        const el = this.el?.querySelector('#mg-timer-val');
        if (el) el.textContent = String(this._seconds).padStart(3, '0');
      }
    }, 1000);
  }

  _stopTimer() {
    clearInterval(this._timerInterval);
    this._timerInterval = null;
  }

  /* ---- Actions ---- */

  reveal(r, c) {
    if (this.state === 'lost' || this.state === 'won') return;
    if (this.revealed[r][c] || this.flagged[r][c]) return;

    if (this.firstClick) {
      this._initArrays();
      this._placeMines(r, c);
      this.state      = 'playing';
      this.firstClick = false;
      this._startTimer();
    }

    if (this.grid[r][c] === -1) {
      this.revealed[r][c] = true;
      this.state = 'lost';
      this._stopTimer();
      this._revealMines();
      this._render();
      return;
    }

    // Patch only the cells that flood-fill reveals
    const before  = this._revealedSnapshot();
    this._flood(r, c);
    const changed = this._changedSince(before);
    this._patchCells(changed);

    // Increment local counter and sync to localStorage
    this._safeClicks++;
    const nt = MG_UNLOCK.recordSafeClick();
    if (nt !== null && typeof window.mgOnUnlock === 'function') window.mgOnUnlock(nt);

    this._updateHUD();

    if (this._checkWin()) {
      this.state = 'won';
      this._stopTimer();
      this._render();
    }
  }

  flag(r, c) {
    if (this.state === 'lost' || this.state === 'won' || this.revealed[r][c]) return;
    this.flagged[r][c] = !this.flagged[r][c];
    this._patchCells([[r, c]]);
    this._updateHUD(); // mine count changes with flags
  }

  /* ---- Internal ---- */

  _flood(r, c) {
    if (r < 0 || r >= this.ROWS || c < 0 || c >= this.COLS) return;
    if (this.revealed[r][c] || this.flagged[r][c] || this.grid[r][c] === -1) return;
    this.revealed[r][c] = true;
    if (this.grid[r][c] === 0)
      for (let dr = -1; dr <= 1; dr++)
        for (let dc = -1; dc <= 1; dc++) this._flood(r + dr, c + dc);
  }

  _revealMines() {
    for (let r = 0; r < this.ROWS; r++)
      for (let c = 0; c < this.COLS; c++)
        if (this.grid[r][c] === -1) this.revealed[r][c] = true;
  }

  _revealedSnapshot() { return this.revealed.map(row => row.slice()); }

  _changedSince(snap) {
    const out = [];
    for (let r = 0; r < this.ROWS; r++)
      for (let c = 0; c < this.COLS; c++)
        if (this.revealed[r][c] && !snap[r][c]) out.push([r, c]);
    return out;
  }

  _checkWin() {
    for (let r = 0; r < this.ROWS; r++)
      for (let c = 0; c < this.COLS; c++)
        if (this.grid[r][c] !== -1 && !this.revealed[r][c]) return false;
    return true;
  }

  _flagCount() {
    return this.flagged.reduce((s, row) => s + row.filter(Boolean).length, 0);
  }

  /* ---- Rendering ---- */

  _numColor(n) {
    return ['', '#0000ff', '#007b00', '#ff0000', '#00007b', '#7b0000', '#007b7b', '#000000', '#7b7b7b'][n] || '#000';
  }

  _face() {
    if (this._clicking && this.state === 'playing') return '😮';
    return { idle: '🙂', playing: '🙂', won: '😎', lost: '😵' }[this.state];
  }

  _cellHtml(r, c) {
    if (this.firstClick)
      return `<td class="mg-cell mg-cell-up" data-r="${r}" data-c="${c}"></td>`;

    const rev  = this.revealed[r][c];
    const flag = this.flagged[r][c];
    const val  = this.grid[r][c];

    if (!rev && flag)
      return `<td class="mg-cell mg-cell-up" data-r="${r}" data-c="${c}">🚩</td>`;
    if (!rev)
      return `<td class="mg-cell mg-cell-up" data-r="${r}" data-c="${c}"></td>`;
    if (val === -1)
      return `<td class="mg-cell mg-cell-mine${this.state === 'lost' ? ' mg-cell-boom' : ''}" data-r="${r}" data-c="${c}">💣</td>`;

    const inner = val > 0
      ? `<span style="color:${this._numColor(val)};font-weight:bold;font-size:13px">${val}</span>`
      : '';
    return `<td class="mg-cell mg-cell-down" data-r="${r}" data-c="${c}">${inner}</td>`;
  }

  // Full rebuild — only on game start, win, or lose
  _render() {
    if (!this.el) return;

    let rows = '';
    for (let r = 0; r < this.ROWS; r++) {
      rows += '<tr>';
      for (let c = 0; c < this.COLS; c++) rows += this._cellHtml(r, c);
      rows += '</tr>';
    }

    this.el.innerHTML = `
      <div class="mg-frame-outer">
        <div class="mg-menubar">
          <div class="mg-menu-item" id="mg-menu-game">Game</div>
          <div class="mg-menu-item">Help</div>
          <div id="mg-menu-dropdown" class="mg-dropdown" style="display:none">
            <div class="mg-dropdown-item" id="mg-new-game-btn">New Game</div>
            <div class="mg-dropdown-sep"></div>
            <div class="mg-dropdown-item mg-dropdown-disabled">Beginner</div>
            <div class="mg-dropdown-item mg-dropdown-disabled">Intermediate</div>
            <div class="mg-dropdown-item mg-dropdown-active">Expert ✓</div>
            <div class="mg-dropdown-sep"></div>
            <div class="mg-dropdown-item" id="mg-reset-progress-btn" style="color:#cc0000">Reset All Progress</div>
          </div>
        </div>
        <div class="mg-frame-inset mg-hud">
          <div style="display:flex;flex-direction:column;align-items:center;">
            <div class="mg-lcd" id="mg-clicks-val">0/6</div>
            <div class="mg-lcd-label">clicks</div>
          </div>
          <button class="mg-face-btn" id="mg-face-btn">${this._face()}</button>
          <div style="display:flex;flex-direction:column;align-items:center;">
            <div class="mg-lcd" id="mg-tiers-val">0/12</div>
            <div class="mg-lcd-label">unlocked</div>
          </div>
        </div>
        <div class="mg-frame-inset mg-grid-wrap">
          <table class="mg-grid" cellspacing="0" cellpadding="0"><tbody>${rows}</tbody></table>
        </div>
      </div>`;

    this._bindEvents();
    this._updateHUD(); // sync counters after full rebuild
  }

  // Patch only cells that changed — no grid rebuild
  _patchCells(coords) {
    const tbody = this.el.querySelector('.mg-grid tbody');
    if (!tbody) return;
    for (const [r, c] of coords) {
      const cell = tbody.querySelector(`[data-r="${r}"][data-c="${c}"]`);
      if (!cell) continue;
      const html = this._cellHtml(r, c);
      const tmp  = document.createElement('table');
      tmp.innerHTML = `<tbody><tr>${html}</tr></tbody>`;
      const newCell = tmp.querySelector('td');
      if (newCell) {
        cell.className = newCell.className;
        cell.innerHTML = newCell.innerHTML;
      }
    }
  }

  // Update HUD elements by ID without touching the grid
  _updateHUD() {
    const progress = this._safeClicks % 3;
    const unlocked = MG_UNLOCK.getUnlockedCount();

    const cEl  = this.el.querySelector('#mg-clicks-val');
    const tEl  = this.el.querySelector('#mg-tiers-val');
    const face = this.el.querySelector('#mg-face-btn');

    if (cEl)  cEl.textContent  = `${progress}/3`;
    if (tEl)  tEl.textContent  = `${unlocked}/12`;
    if (face) face.textContent = this._face();
  }

  _bindEvents() {
    this.el.querySelectorAll('.mg-cell').forEach(cell => {
      const r = +cell.dataset.r, c = +cell.dataset.c;

      cell.addEventListener('mousedown', e => {
        if (e.button === 0) { this._clicking = true;  this._updateFace(); }
      });
      cell.addEventListener('mouseup', e => {
        if (e.button === 0) { this._clicking = false; this._updateFace(); }
      });
      cell.addEventListener('click',       ()  => this.reveal(r, c));
      cell.addEventListener('contextmenu', e   => { e.preventDefault(); this.flag(r, c); });
    });

    this.el.querySelector('#mg-face-btn')?.addEventListener('click', () => this._reset());

    const gameMenu = this.el.querySelector('#mg-menu-game');
    const dropdown = this.el.querySelector('#mg-menu-dropdown');
    gameMenu?.addEventListener('click', e => {
      e.stopPropagation();
      dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
    });

    this.el.querySelector('#mg-new-game-btn')?.addEventListener('click', () => {
      dropdown.style.display = 'none';
      this._reset();
    });

    this.el.querySelector('#mg-reset-progress-btn')?.addEventListener('click', () => {
      dropdown.style.display = 'none';
      localStorage.removeItem(MG_UNLOCK.STORAGE_KEY);
      this._safeClicks = 0;
      this._reset();
      if (typeof window.mgUpdateUnlockCount === 'function') window.mgUpdateUnlockCount();
    });

    document.addEventListener('click', () => {
      if (dropdown) dropdown.style.display = 'none';
    }, { once: true });

    document.addEventListener('mouseup', () => {
      this._clicking = false;
      this._updateFace();
    });
  }

  _updateFace() {
    const btn = this.el?.querySelector('#mg-face-btn');
    if (btn) btn.textContent = this._face();
  }
}
